import React, { useState, useRef, useEffect } from 'react';
import {
  Mail,
  Phone,
  Calendar,
  Download,
  Coffee,
  Brain,
  CheckCircle,
  ArrowRight,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { sendContactFormToTelegram } from '../services/TelegramService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import ScheduleBooking from "./scheduling/ScheduleBooking";

const LeadGenerationSection = () => {
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [activeTab, setActiveTab] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: '',
    budget: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchLeadMagnets = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/lead-magnets`);
        setLeadMagnets(response.data);
      } catch (error) {
        console.error('Error fetching lead magnets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeadMagnets();
  }, [backendUrl]);

  // Ref to keep track of current form values
  const formRef = useRef(formData);

  const handleInputChange = (e) => {
    const newData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    formRef.current = newData;
    setFormData(newData);
  };

  const handleSubmit = async (e, type = 'contact') => {
    e.preventDefault();
    setIsSubmitting(true);

    if (type === 'contact') {
      const currentFormData = formRef.current;
      try {
        const telegramSuccess = await sendContactFormToTelegram(
          currentFormData.name,
          currentFormData.email,
          currentFormData.company,
          currentFormData.service,
          currentFormData.budget,
          currentFormData.message
        );

        toast({
          title: telegramSuccess ? "Success!" : "Message Sent!",
          description: "Thank you for your interest. We'll contact you within 24 hours.",
        });
      } catch (error) {
        toast({
          title: "Message Sent!",
          description: "Thank you for your interest. We'll contact you within 24 hours.",
        });
      }
    } else {
      toast({
        title: "Success!",
        description: "Resource sent to your email. Check your inbox!",
      });
    }

    const resetData = { name: '', email: '', company: '', message: '', service: '', budget: '' };
    setFormData(resetData);
    formRef.current = resetData;
    setIsSubmitting(false);
  };

  if (loading || leadMagnets.length === 0) return null;

  const standardMagnets = leadMagnets.filter(m => !m.quirky);
  const quirkyMagnets = leadMagnets.filter(m => m.quirky);

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-silver/60 text-sm font-mono tracking-widest uppercase mb-4">
            Let's Connect
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-silver mb-6">
            Ready to Transform
            <span className="block gradient-text">Your Business?</span>
          </h2>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Choose how you'd like to start your journey with us. Whether you prefer
            a formal consultation or something more engaging, we're here to help.
          </p>
        </div>

        {/* Lead Generation Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-800 p-1 rounded-full">
            <button
              onClick={() => setActiveTab('standard')}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeTab === 'standard'
                ? 'bg-silver text-black'
                : 'text-gray-400 hover:text-silver'
                }`}
            >
              Professional Approach
            </button>
            <button
              onClick={() => setActiveTab('quirky')}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeTab === 'quirky'
                ? 'bg-silver text-black'
                : 'text-gray-400 hover:text-silver'
                }`}
            >
              Interactive Experience
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Lead Magnets */}
          <div>
            <h3 className="text-2xl font-bold text-silver mb-8">
              {activeTab === 'standard' ? 'Professional Resources' : 'Interactive Experiences'}
            </h3>
            <div className="space-y-6">
              {(activeTab === 'standard' ? standardMagnets : quirkyMagnets).map((magnet) => (
                <div
                  key={magnet.id}
                  className="group bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-gray-700 hover:border-gray-600 transition-all duration-300 card-hover-effect"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-silver/20 rounded-lg">
                      {magnet.type === 'standard' ? (
                        magnet.title.includes('Assessment') ? <CheckCircle size={24} className="text-silver" /> :
                          magnet.title.includes('Guide') ? <Download size={24} className="text-silver" /> :
                            <Mail size={24} className="text-silver" />
                      ) : (
                        magnet.title.includes('Quiz') ? <Brain size={24} className="text-silver" /> :
                          magnet.title.includes('Coffee') ? <Coffee size={24} className="text-silver" /> :
                            <Zap size={24} className="text-silver" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-silver mb-2 group-hover:text-white transition-colors duration-300">
                        {magnet.title}
                      </h4>
                      <p className="text-gray-400 mb-4 leading-relaxed">
                        {magnet.description}
                      </p>
                      {magnet.cta.includes('Assessment') || magnet.cta.includes('Session') || magnet.cta.includes('Quiz') ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className="flex items-center gap-2 text-silver hover:text-white font-medium transition-colors duration-300"
                            >
                              {magnet.cta}
                              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[500px] bg-black border-gray-800 p-8 pt-12">
                            <DialogHeader>
                              <DialogTitle className="text-2xl font-bold text-silver">Schedule your {magnet.title}</DialogTitle>
                            </DialogHeader>
                            <ScheduleBooking />
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <button
                          onClick={(e) => handleSubmit(e, 'resource')}
                          className="flex items-center gap-2 text-silver hover:text-white font-medium transition-colors duration-300"
                        >
                          {magnet.cta}
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="glass-morphism rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-silver mb-6">Get Started Today</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-silver text-sm font-medium mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-silver text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-silver text-sm font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label className="block text-silver text-sm font-medium mb-2">
                      Service Interest
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
                    >
                      <option value="">Select a service</option>
                      <option value="cloud">Cloud Solutions</option>
                      <option value="software">Custom Software</option>
                      <option value="infrastructure">Infrastructure</option>
                      <option value="consulting">IT Strategy</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-silver text-sm font-medium mb-2">
                    Project Budget Range
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
                  >
                    <option value="">Select budget range</option>
                    <option value="50k-100k">$50K - $100K</option>
                    <option value="100k-250k">$100K - $250K</option>
                    <option value="250k-500k">$250K - $500K</option>
                    <option value="500k+">$500K+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-silver text-sm font-medium mb-2">
                    Tell us about your project
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver resize-none"
                    placeholder="Describe your project goals, challenges, or questions..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-silver text-black py-3 rounded-lg font-semibold hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 text-gray-400">
                <Mail size={20} className="text-silver" />
                <span>hello@hara-xy.com</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Phone size={20} className="text-silver" />
                <span>+91 7676 984958</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadGenerationSection;